import { getXdkWindow } from '@pcmp/xdk-types';

import type { CacheStorageKeys } from './cache.utils';
import type { Authentication } from './types/auth.types';
import type { Cart } from './types/cart.types';
import { type CoworkerPreferences, type CoworkerProfile, StorePreferenceKeys } from './types/coworker.types';
import type { CustomerProfile, CustomerProfileQuery } from './types/customer.types';
import type { Notifier } from './types/notifier.types';
import type { OrderHistory, SaljaWindow } from './types/window.types';

declare const window: SaljaWindow;

/* v8 ignore start */
export const getCoworkerProfile = (): CoworkerProfile | undefined => getXdkWindow()?.coworkerprofile;
export const getAppAuth = (): Authentication | undefined => getXdkWindow()?.authentication;
export const getNotifier = (): Notifier | undefined => getXdkWindow()?.notifier;
export const getCart = (): Cart | undefined => getXdkWindow()?.cart;
export const getCustomerProfile = (): CustomerProfile | undefined => window.salja.customerProfile;
export const getOrderHistory = (): OrderHistory | undefined => window.salja.orderHistory;
export const getCacheStorageKeys = (): CacheStorageKeys[] => window.salja.cacheStorageKeys;
export const getManualPaymentsFragment = () => window.salja.manualPayments;
export const getOrdersCoworkerPreferences = (): CoworkerPreferences | undefined =>
    window.salja.orders?.coworkerPreferences;
/* v8 ignore stop */

export const getAppLocale = (): string => {
    const { country, language } = getCountryLanguage();
    return `${language}-${country.toUpperCase()}`;
};

export const getCountryLanguage = (): { country: string; language: string } => {
    const [_, country, language] = window.location.pathname.split('/');
    if (!country || !language) {
        throw new Error('Country or language not found');
    }

    return { country, language };
};

export const searchCustomer = async (
    customerType: CustomerProfileQuery['customerType'],
    partyUID = '',
    countryCode = ''
) => {
    const customerProfile = getCustomerProfile();

    if (customerProfile?.searchCustomer) {
        const result = await customerProfile.searchCustomer({ customerType, partyUID }, countryCode);

        return result?.customerSelection;
    }

    throw new Error('Customer profile or searchCustomer function not found');
};

export const getStoreSupportedLocales = () => {
    const coworkerProfile = getCoworkerProfile();

    if (coworkerProfile?.data?.storePreferencesConfig?.coworkerInfo?.supportedLocales) {
        return [...coworkerProfile.data.storePreferencesConfig.coworkerInfo.supportedLocales];
    }

    return [];
};

export const getStorePrimaryLocale = () => {
    const preferences = getCoworkerProfile()?.data?.storePreferencesConfig?.preferences ?? [];

    const primaryLocaleValue = preferences.find(
        (preference) => preference.name === StorePreferenceKeys.PreferredLocale
    )?.value;

    if (typeof primaryLocaleValue !== 'string' || !primaryLocaleValue) {
        return;
    }

    const transformLocale = (separator: string) => {
        const [country, language] = primaryLocaleValue.split(separator);
        return `${language}-${country}`;
    };

    if (primaryLocaleValue.includes('_')) {
        return transformLocale('_');
    }

    if (primaryLocaleValue.includes('-')) {
        return transformLocale('-');
    }

    return primaryLocaleValue;
};
